import React, { useState, useEffect } from 'react';
import { metadataAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Calendar } from 'lucide-react';
import { formatDateTime } from '../utils/helpers';

export default function HolidayCalendarPage() {
  const { isStaff } = useAuth();
  const [axsHolidays, setAxsHolidays] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientHolidays, setClientHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  // Always load Axslogic holidays (these drive SLA)
  useEffect(() => {
    metadataAPI.getAxslogicHolidays()
      .then(r => setAxsHolidays(r.data))
      .catch(() => setAxsHolidays([]))
      .finally(() => setLoading(false));
    if (isStaff) {
      metadataAPI.getClients().then(r => setClients(r.data)).catch(() => {});
    }
  }, [isStaff]);

  // Load client holidays when staff selects a client
  useEffect(() => {
    if (!selectedClientId) { setClientHolidays([]); return; }
    metadataAPI.getClientHolidays(selectedClientId)
      .then(r => setClientHolidays(r.data))
      .catch(() => setClientHolidays([]));
  }, [selectedClientId]);

  return (
    <div>
      <div className="page-header">
        <h1>Holiday Calendar</h1>
        <p>Axslogic working holidays — SLA deadlines exclude these dates for all clients</p>
      </div>

      {loading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
      ) : axsHolidays.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
          <Calendar size={32} style={{ marginBottom: 8, opacity: 0.5 }} />
          <p>No Axslogic holidays configured.</p>
          <p style={{ fontSize: 12 }}>Holidays are synced from the Winnow Hub. Run Hub Sync from Configuration.</p>
        </div>
      ) : (
        <HolidayTable holidays={axsHolidays} title="Axslogic Holidays (SLA applicable)" />
      )}

      {/* Staff can additionally view client holidays for reference */}
      {isStaff && clients.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)' }}>View client holidays (reference only):</span>
            <select className="form-select" style={{ width: 280 }} value={selectedClientId}
              onChange={e => setSelectedClientId(e.target.value)}>
              <option value="">Select client...</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.client_name} ({c.client_code})</option>
              ))}
            </select>
          </div>
          {selectedClientId && clientHolidays.length > 0 && (
            <HolidayTable holidays={clientHolidays} title={`Client Holidays (reference)`} muted />
          )}
          {selectedClientId && clientHolidays.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No holidays found for this client.</p>
          )}
        </div>
      )}
    </div>
  );
}

function HolidayTable({ holidays, title, muted }) {
  // Group by month
  const grouped = {};
  holidays.forEach(h => {
    const month = h.date.substring(0, 7);
    if (!grouped[month]) grouped[month] = [];
    grouped[month].push(h);
  });

  const monthName = (ym) => {
    const [y, m] = ym.split('-');
    return new Date(y, parseInt(m) - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };
  const dayName = (dateStr) => new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' });
  const formatDate = (dateStr) => new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: muted ? 0.75 : 1 }}>
      {Object.entries(grouped).map(([month, items]) => (
        <div key={month} className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>{monthName(month)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {items.map((h, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '8px 12px', background: 'var(--color-bg-subtle, #f9fafb)', borderRadius: 6,
              }}>
                <div style={{ minWidth: 70, fontSize: 13, fontWeight: 600, color: 'var(--color-primary)' }}>{formatDate(h.date)}</div>
                <div style={{ minWidth: 36, fontSize: 12, color: 'var(--color-text-muted)' }}>{dayName(h.date)}</div>
                <div style={{ flex: 1, fontSize: 13 }}>{h.name}</div>
                <span className="badge" style={{
                  fontSize: 10, padding: '2px 8px',
                  background: h.type === 'BANK' ? '#dbeafe' : h.type === 'COMPANY' ? '#e0e7ff' : '#d1fae5',
                  color: h.type === 'BANK' ? '#1d4ed8' : h.type === 'COMPANY' ? '#3730a3' : '#065f46',
                }}>{h.type}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
